/**
 * Anhang II der Baustellenverordnung — die besonders gefaehrlichen Arbeiten,
 * so wie das Handbuch sie je Gewerk ankreuzt.
 *
 * 1:1 portiert aus `sige-plan/src/config/anhang-ii.ts` der Quell-App. Die
 * Wortlaute bleiben unveraendert: sie stehen so im gedruckten Handbuch, und ein
 * umformulierter Katalogeintrag laesst sich mit dem Bestand nicht mehr
 * abgleichen.
 *
 * Der Katalog hat ELF Wortlaute, der Anhang II ZEHN Nummern: Nr. 1 und Nr. 2
 * sind hier in je zwei Zeilen aufgeteilt, Nr. 7 und Nr. 8 stehen in einer. Die
 * Zuordnung Wortlaut ↔ Nummer ↔ Vorankuendigungs-Id ↔ Planungsmerkmal haelt
 * `data/anhang2Bruecke.ts` an EINER Stelle; diese Datei leitet nur daraus ab.
 *
 * WARUM im Repo und nicht in der Datenbank: die Migration setzt bewusst KEINEN
 * CHECK auf `handbuch_gewerke.anhang_ii`. Ein CHECK waere die zweite Stelle,
 * an der derselbe Katalog gepflegt werden muesste, und die beiden liefen
 * auseinander. Derselbe Grund wie beim Fragenkatalog der Planungsphase: waechst
 * der Katalog, aendert sich NICHT, was in einem abgeschlossenen Projekt stand.
 *
 * Reine Daten — kein DOM, kein React, keine Supabase.
 */

import {
    handbuchFuerIds,
    labelFuerId,
    nummerFuerId,
    nummernFuerHandbuch,
} from '../../data/anhang2Bruecke';

/**
 * Die Eintraege in Katalogreihenfolge (nach Anhang-II-Nummer).
 *
 * `as const` mit Absicht: der Wortlaut IST der Schluessel. Er wird so in
 * `handbuch_gewerke.anhang_ii` (text[]) gespeichert. Eine zusaetzliche
 * Codespalte waere eine dritte Wahrheit ueber dieselbe Liste.
 *
 * NICHT derselbe Schluessel wie in der Vorankuendigung: dort stehen in
 * `selectedDangerousWorks` die Ids aus `DANGEROUS_WORKS` (`absturz`,
 * `versinken`, …). Wer die Vorankuendigung ins Handbuch uebersetzen will, geht
 * ueber `anhangIIAusVorankuendigung` — ein Id-Vergleich mit dieser Liste
 * findet nie etwas.
 */
export const ANHANG_II_ARBEITEN = [
    'Absturzgefahr > 7m',
    'Verschüttungsgefahr > 5m (Baugruben/Gräben)',
    'Gefahrstoffe Kategorie 1A/1B (karzinogen, mutagen, reproduktionstoxisch)',
    'Biologische Arbeitsstoffe Risikogruppe 3 oder 4',
    'Ionisierende Strahlung (Kontroll-/Überwachungsbereiche)',
    'Arbeiten in < 5m Abstand von Hochspannungsleitungen',
    'Unmittelbare Gefahr des Ertrinkens',
    'Brunnenbau, unterirdische Erdarbeiten, Tunnelbau',
    'Arbeiten mit Tauchgeräten / Druckluft',
    'Arbeiten mit Sprengstoff',
    'Auf-/Abbau von Massivbauelementen mit kraftbetriebenen Hebezeugen',
] as const;

export type AnhangIIArbeit = (typeof ANHANG_II_ARBEITEN)[number];

/** Position im Katalog (0-basiert); -1 fuer einen fremden Wortlaut. */
const katalogIndex = (arbeit: string): number =>
    (ANHANG_II_ARBEITEN as readonly string[]).indexOf(arbeit);

/**
 * Gehoert der Wortlaut zum Katalog?
 *
 * Gebraucht wird das beim Einlesen von Bestandsdaten: `anhang_ii` ist text[]
 * ohne CHECK, dort kann also auch ein Wortlaut aus einer aelteren Katalog-
 * fassung stehen. Solche Eintraege werden ANGEZEIGT und nicht stillschweigend
 * verworfen — sonst verschwindet eine einmal getroffene Feststellung des
 * SiGeKo, weil jemand den Katalog umformuliert hat.
 */
export const istBekannteAnhangIIArbeit = (arbeit: string): arbeit is AnhangIIArbeit =>
    katalogIndex(arbeit) >= 0;

/**
 * Alle Nummern im Anhang II BaustellV, unter die der Wortlaut faellt
 * (aufsteigend). Meist eine; „Tauchgeraete / Druckluft" sind Nr. 7 und 8.
 * Leer fuer einen fremden Wortlaut.
 */
export const anhangIINummern = (arbeit: string): number[] => nummernFuerHandbuch(arbeit);

/**
 * Nummer im Anhang II BaustellV (1-10), wie sie im Handbuch zitiert wird;
 * bei zwei Nummern die kleinere, `null` fuer einen fremden Wortlaut.
 *
 * Frueher stand hier die Katalogposition (1-11) — die stimmte ab dem dritten
 * Eintrag nicht mehr mit dem Gesetz ueberein („5. Ionisierende Strahlung"
 * statt Nr. 3). Fuer die Anzeige mit beiden Nummern: `anhangIINummerText`.
 */
export const anhangIINummer = (arbeit: string): number | null => {
    const nummern = anhangIINummern(arbeit);
    return nummern.length ? nummern[0] : null;
};

/** „3", „7/8" — die Nummer(n) fuer die Anzeige; `null` fuer einen fremden Wortlaut. */
export const anhangIINummerText = (arbeit: string): string | null => {
    const nummern = anhangIINummern(arbeit);
    return nummern.length ? nummern.join('/') : null;
};

/**
 * Sortiert eine Auswahl in Katalogreihenfolge; unbekannte Wortlaute haengen
 * hinten an, in ihrer urspruenglichen Reihenfolge.
 *
 * Doppelte Eintraege fallen weg — dieselbe Arbeit zweimal anzukreuzen ist
 * keine zusaetzliche Aussage.
 */
export const sortiereAnhangII = (auswahl: string[]): string[] => {
    const gesehen = new Set<string>();
    const bekannt: string[] = [];
    const fremd: string[] = [];
    auswahl.forEach(a => {
        const wert = (a || '').trim();
        if (!wert || gesehen.has(wert)) return;
        gesehen.add(wert);
        (istBekannteAnhangIIArbeit(wert) ? bekannt : fremd).push(wert);
    });
    bekannt.sort((a, b) => katalogIndex(a) - katalogIndex(b));
    return [...bekannt, ...fremd];
};

/**
 * Uebersetzt `vorankuendigung.selectedDangerousWorks` (Ids aus
 * `DANGEROUS_WORKS`) in Handbuch-Wortlaute, sortiert und ohne Doppelte.
 *
 * Regeln, damit nichts stillschweigend verschwindet:
 *   - Eine Id mit Handbuch-Wortlaut(en) wird zu genau diesen („absturz" →
 *     nur „Absturzgefahr > 7m", nicht auch die Verschuettung).
 *   - Eine Id OHNE Handbuch-Wortlaut (heute `explosiv`) wird zu ihrem Label
 *     aus der Vorankuendigung — sichtbar, aber als fremder Wortlaut.
 *   - Ein Wert, der schon ein Katalog-Wortlaut ist, bleibt wie er ist.
 *   - Ein unbekannter Wert bleibt wie er ist — so wie `anhang_ii` ihn auch
 *     aus einer aelteren Katalogfassung anzeigt.
 */
export const anhangIIAusVorankuendigung = (
    werte: readonly (string | null | undefined)[] | null | undefined,
): string[] => {
    const raus: string[] = [];
    (werte || []).forEach(w => {
        const wert = (w || '').trim();
        if (!wert) return;
        if (istBekannteAnhangIIArbeit(wert) || nummerFuerId(wert) === null) {
            raus.push(wert);
            return;
        }
        const wortlaute = handbuchFuerIds([wert]);
        if (wortlaute.length) raus.push(...wortlaute);
        else raus.push(labelFuerId(wert) ?? wert);
    });
    return sortiereAnhangII(raus);
};
